import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ONBOARDING_STORAGE,
  clearOnboardingStorage,
  getProgressKey
} from "../utils/onboardingStorage";
import { api } from "../api/api";
import "../styles/OnboardingAssistant.css";
import OnboardingSubSteps from "./OnboardingSubSteps";
import OnboardingDemoModal from "./OnboardingDemoModal";

const ROLE_OPTIONS = [
  {
    id: "customer",
    title: "Hizmet Alan",
    description: "İlan açar, teklif alır ve hizmet satın alırım."
  },
  {
    id: "provider",
    title: "Hizmet Veren",
    description: "Hizmet sunar, ilanlara teklif veririm."
  },
  {
    id: "both",
    title: "Her İkisi",
    description: "Hem hizmet alır hem de hizmet veririm."
  }
];

const getBaseSteps = ({ counts }) => [
  {
    id: "login",
    title: "Kayıt Ol / Giriş Yap",
    description: "Hesabını oluştur veya mevcut hesabınla giriş yap.",
    required: true,
    autoComplete: ({ user }) => Boolean(user?.id),
    autoEnforced: true,
    getAction: ({ user }) => ({
      label: user?.id ? "Profilim" : "Giriş / Kayıt",
      to: user?.id ? "/userProfile" : "/login"
    })
  },
  {
    id: "profile",
    title: "Profilini Tamamla",
    description: "İletişim ve güven bilgilerini düzenleyerek profilini güçlendir.",
    required: true,
    action: { label: "Profilimi Düzenle", to: "/userProfile", tour: "profile-edit" },
    autoComplete: ({ user, counts, flow }) => {
      const basicInfo = Boolean(user?.hasPhone && counts.addresses > 0);
      // Providers must have identity approved, customers can skip for now
      if (flow === 'provider') {
        return basicInfo && (user?.identity_status === 'approved' || user?.hasIdentity);
      }
      return basicInfo;
    },
    details: [
      { id: "phone", label: "Telefon Doğrula", done: (u) => !!u?.phone || !!u?.hasPhone, tour: "profile-edit" },
      { id: "identity", label: "Güven Rozeti Al (Kimlik)", done: (u) => u?.identity_status === 'approved' || !!u?.hasIdentity, tour: "profile-identity" },
      { id: "address", label: "Adres Tanımla", done: (u, c) => c.addresses > 0, tour: "profile-edit" }
    ]
  },
  {
    id: "agreements",
    title: "Gerekli Sözleşmeleri Onayla",
    description: "KVKK ve rolüne uygun sözleşmeleri onayla.",
    required: true,
    autoComplete: ({ counts }) => counts.pendingContracts !== null && counts.pendingContracts.length === 0,
    action: { label: "Belgeleri Onayla", to: "/userProfile", tour: "profile-edit" },
    details: [
      { id: "kvkk", label: "KVKK Aydınlatma Metni", done: (u, c) => !c.pendingContracts?.some(p => p.slug === 'kvkk'), tour: "profile-edit" },
      { id: "riza", label: "Açık Rıza Beyanı", done: (u, c) => !c.pendingContracts?.some(p => p.slug === 'acik-riza'), tour: "profile-edit" },
      { id: "sozlesme", label: "Kullanıcı Sözleşmesi", done: (u, c) => !c.pendingContracts?.some(p => p.slug === 'hizmet-alan' || p.slug === 'hizmet-veren'), tour: "profile-edit" }
    ]
  },
  {
    id: "notifications",
    title: "Bildirimleri Aç",
    description: "Teklif ve mesajları kaçırmamak için bildirimleri etkinleştir.",
    required: false,
    autoComplete: ({ user }) => Boolean(user?.fcmToken || user?.chat_notify_push || user?.push_notify || user?.push_token),
    action: { label: "Bildirim Ayarları", to: "/settings" }
  }
];

const CUSTOMER_STEPS = ({ counts }) => [
  {
    id: "post_task",
    title: "İlan Oluştur",
    description: "Kategori ve hizmet seçip ilanını yayınla.",
    required: true,
    autoComplete: ({ counts }) => counts.tasks > 0,
    action: { label: "Hemen İlan Ver", to: "/", tour: "search-bar" },
    demoType: "post_task"
  },
  {
    id: "review_and_accept",
    title: "Teklifleri İncele & Kabul Et",
    description: "Gelen teklifleri karşılaştır, sözleşmeleri onayla ve ödemeye geç.",
    required: true,
    action: { label: "Gelen Teklifler", to: "/availableTasks?tab=offers" },
    demoType: "offers"
  },
  {
    id: "manage_and_complete",
    title: "İş Sürecini Yönet",
    description: "İş detaylarını takip et, iletişim kur, tamamla ve değerlendir.",
    required: true,
    action: { label: "İşlerim", to: "/myJobs" },
    demoType: "jobs"
  }
];

const PROVIDER_STEPS = ({ counts }) => [
  {
    id: "add_services",
    title: "Hizmetlerini Ekle",
    description: "Uzmanlık alanlarını profiline ekle.",
    required: true,
    autoComplete: ({ counts }) => counts.services > 0,
    action: { label: "Profil", to: "/userProfile" }
  },
  {
    id: "tax_profile",
    title: "Vergi Bilgilerini Tamamla",
    description: "Vergi mükellefiysen bilgilerini ekle.",
    required: false,
    autoComplete: ({ user }) => user?.tax_status === 'verified',
    action: { label: "Profil", to: "/userProfile" }
  },
  {
    id: "browse_tasks",
    title: "Güncel İlanları İncele",
    description: "Uygun ilanları filtreleyip detaylarını gör.",
    required: true,
    action: { label: "Güncel İlanlar", to: "/availableTasks?tab=tasks" },
    demoType: "browse_tasks"
  },
  {
    id: "send_offer",
    title: "Teklif Gönder",
    description: "Fiyat ve not ekleyerek teklifini ilet.",
    required: true,
    autoComplete: ({ counts }) => counts.offers > 0,
    action: { label: "Teklif Ver", to: "/availableTasks?tab=tasks" },
    demoType: "send_offer"
  },
  {
    id: "track_offer",
    title: "Tekliflerini Yönet",
    description: "Tekliflerini güncelle veya geri çek.",
    required: true,
    action: { label: "Tekliflerim", to: "/availableTasks?tab=myOffers" }
  },
  {
    id: "complete_job",
    title: "Hizmeti Tamamla",
    description: "İş bitince sistemden tamamlandı bildirimi gönder.",
    required: true,
    action: { label: "İşlerim", to: "/myJobs" },
    demoType: "jobs"
  },
  {
    id: "review_customer",
    title: "Müşteriyi Değerlendir",
    description: "Deneyimini puanla ve yorum bırak.",
    required: false,
    action: { label: "Değerlendirme", to: "/myJobs" },
    demoType: "jobs"
  }
];

const readProgress = (flow) => {
  try {
    const raw = localStorage.getItem(getProgressKey(flow));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveProgress = (flow, next) => {
  localStorage.setItem(getProgressKey(flow), JSON.stringify(next));
};

export default function OnboardingAssistant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cardRef = useRef(null);

  const [neverShow, setNeverShow] = useState(() =>
    localStorage.getItem(ONBOARDING_STORAGE.neverShow) === "1"
  );
  const [isOpen, setIsOpen] = useState(() => {
    const disabled = localStorage.getItem(ONBOARDING_STORAGE.neverShow) === "1";
    if (disabled) return false;
    return localStorage.getItem(ONBOARDING_STORAGE.minimized) !== "1";
  });
  const [role, setRole] = useState(
    () => localStorage.getItem(ONBOARDING_STORAGE.role) || ""
  );
  const [activeFlow, setActiveFlow] = useState(() =>
    localStorage.getItem(ONBOARDING_STORAGE.activeFlow) || "customer"
  );

  const flow = role === "both" ? activeFlow : role || "customer";
  const [progress, setProgress] = useState(() => readProgress(flow));
  const [counts, setCounts] = useState({ addresses: 0, tasks: 0, services: 0, offers: 0, pendingContracts: null });
  const [demoType, setDemoType] = useState(null);

  useEffect(() => {
    if (!user?.id || !isOpen) return;

    const fetchCounts = async () => {
      try {
        const [locRes, taskRes, serviceRes, offerRes, pendingRes] = await Promise.all([
          api.get(`/users/getLocations/${user.id}`),
          api.get(`/tasks/getTasksByUser/${user.id}`),
          api.get(`/users/getUserServices/${user.id}`),
          api.post(`/tasks/getApplications/${user.id}`, { limit: 10, offset: 0 }),
          api.get(`/legal-contracts/user/pending`)
        ]);

        setCounts({
          addresses: locRes.data.locations?.length || 0,
          tasks: taskRes.data?.length || 0,
          services: serviceRes.data.services?.length || 0,
          offers: offerRes.data.Applications?.length || 0,
          pendingContracts: pendingRes.data.pending || []
        });
      } catch (err) {
        console.error("Error fetching onboarding counts:", err);
      }
    };
    fetchCounts();
  }, [user?.id, isOpen]);

  useEffect(() => {
    setProgress(readProgress(flow));
  }, [flow]);

  useEffect(() => {
    if (neverShow || isOpen) return;
    if (localStorage.getItem(ONBOARDING_STORAGE.minimized) === "1") return;
    const timer = setTimeout(() => setIsOpen(true), 700);
    return () => clearTimeout(timer);
  }, [neverShow, isOpen]);

  useEffect(() => {
    const handleOpen = () => {
      localStorage.removeItem(ONBOARDING_STORAGE.neverShow);
      localStorage.setItem(ONBOARDING_STORAGE.minimized, "0");
      setNeverShow(false);
      setIsOpen(true);
    };

    const handleDisable = () => {
      localStorage.setItem(ONBOARDING_STORAGE.neverShow, "1");
      setNeverShow(true);
      setIsOpen(false);
    };

    const handleReset = () => {
      clearOnboardingStorage();
      setRole("");
      setActiveFlow("customer");
      setProgress({});
      setNeverShow(false);
      setIsOpen(true);
    };

    window.addEventListener("onboarding:open", handleOpen);
    window.addEventListener("onboarding:disable", handleDisable);
    window.addEventListener("onboarding:reset", handleReset);
    return () => {
      window.removeEventListener("onboarding:open", handleOpen);
      window.removeEventListener("onboarding:disable", handleDisable);
      window.removeEventListener("onboarding:reset", handleReset);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      // Only minimize if the click is truly outside the card ref AND not on the demo modal
      if (cardRef.current && !cardRef.current.contains(e.target) && !e.target.closest('.demo-overlay')) {
        handleMinimize();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const steps = useMemo(() => {
    const base = getBaseSteps({ counts });
    if (!role) return base;
    if (role === "both") {
      return [
        ...base,
        { id: "divider_customer", title: "🏠 Hizmet Alan Adımları", type: "divider" },
        ...CUSTOMER_STEPS({ counts }),
        { id: "divider_provider", title: "🛠️ Hizmet Veren Adımları", type: "divider" },
        ...PROVIDER_STEPS({ counts })
      ];
    }
    if (flow === "provider") return [...base, ...PROVIDER_STEPS({ counts })];
    return [...base, ...CUSTOMER_STEPS({ counts })];
  }, [flow, role, counts]);

  const resolveAction = (step) => {
    if (step.getAction) {
      return step.getAction({ user });
    }
    return step.action || null;
  };

  const handleShowMe = (to, tourKey) => {
    navigate(`${to}?tour=${tourKey}`);
    handleMinimize();
  };

  const isStepDone = (step) => {
    if (step.type === "divider") return false;
    if (step.autoComplete) {
      const autoDone = step.autoComplete({ user, counts, flow });
      if (step.autoEnforced) return autoDone;
      if (autoDone) return true;
    }
    return Boolean(progress[step.id]);
  };

  const requiredSteps = steps.filter((step) => step.required);
  const requiredDone = requiredSteps.filter((step) => isStepDone(step)).length;
  const requiredTotal = requiredSteps.length;
  const progressPercent = requiredTotal
    ? Math.round((requiredDone / requiredTotal) * 100)
    : 0;

  const nextRequiredStep = steps.find(
    (step) => step.required && !isStepDone(step)
  );

  const handleToggleStep = (step) => {
    if (step.autoEnforced) return;
    const next = { ...progress, [step.id]: !progress[step.id] };
    setProgress(next);
    saveProgress(flow, next);
  };

  const handleSelectRole = (nextRole) => {
    setRole(nextRole);
    localStorage.setItem(ONBOARDING_STORAGE.role, nextRole);
    if (nextRole === "both") {
      setActiveFlow("customer");
      localStorage.setItem(ONBOARDING_STORAGE.activeFlow, "customer");
    }
  };

  const handleChangeFlow = (nextFlow) => {
    setActiveFlow(nextFlow);
    localStorage.setItem(ONBOARDING_STORAGE.activeFlow, nextFlow);
  };

  const handleMinimize = () => {
    localStorage.setItem(ONBOARDING_STORAGE.minimized, "1");
    setIsOpen(false);
  };

  const handleOpen = () => {
    localStorage.setItem(ONBOARDING_STORAGE.minimized, "0");
    setIsOpen(true);
  };

  const handleNeverShow = () => {
    localStorage.setItem(ONBOARDING_STORAGE.neverShow, "1");
    setNeverShow(true);
    setIsOpen(false);
  };

  const handleResetRole = () => {
    localStorage.removeItem(ONBOARDING_STORAGE.role);
    localStorage.removeItem(ONBOARDING_STORAGE.activeFlow);
    setRole("");
  };

  const handleNavigate = (target) => {
    if (!target) return;
    navigate(target);
  };

  if (neverShow) return null;

  return (
    <>
      <div className="onboarding-assistant" aria-live="polite">
        {isOpen ? (
          <div
            className="onboarding-card"
            role="dialog"
            aria-label="Başlangıç Rehberi"
            ref={cardRef}
          >
            {/* ... rest of the card ... */}
            <div className="onboarding-header">
              <div>
                <p className="onboarding-title">Başlangıç Rehberi</p>
                <p className="onboarding-subtitle">
                  Sistemdeki adımları hızlıca tamamlaman için yanındayım.
                </p>
              </div>
              <div className="onboarding-header-actions">
                <button
                  type="button"
                  className="onboarding-icon-btn"
                  onClick={handleMinimize}
                  aria-label="Küçült"
                >
                  –
                </button>
              </div>
            </div>

            <div className="onboarding-progress">
              <div className="onboarding-progress-info">
                <span>Zorunlu adımlar</span>
                <span>{requiredDone} / {requiredTotal}</span>
              </div>
              <div className="onboarding-progress-bar">
                <span style={{ width: `${progressPercent}%` }} />
              </div>
            </div>

            <div className="onboarding-body">
              {!role ? (
                <div className="onboarding-role-select">
                  <p className="onboarding-section-title">
                    Nasıl devam etmek istiyorsunuz?
                  </p>
                  <div className="onboarding-role-grid">
                    {ROLE_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className="onboarding-role-card"
                        onClick={() => handleSelectRole(option.id)}
                      >
                        <span className="onboarding-role-title">{option.title}</span>
                        <span className="onboarding-role-desc">{option.description}</span>
                      </button>
                    ))}
                  </div>
                  <p className="onboarding-hint">
                    Bu seçim sadece yönlendirme içindir, istediğiniz zaman değiştirebilirsiniz.
                  </p>
                </div>
              ) : (
                <>
                  <div className="onboarding-flow-header">
                    <div>
                      <p className="onboarding-section-title">Akış Seçimi</p>
                      <p className="onboarding-section-subtitle">
                        Rolüne göre adımları takip et.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="onboarding-link-btn"
                      onClick={handleResetRole}
                    >
                      Rolü Değiştir
                    </button>
                  </div>

                  {role === "both" && (
                    <div className="onboarding-flow-wrapper">
                      <div className="onboarding-flow-toggle">
                        <button
                          type="button"
                          className={activeFlow === "customer" ? "active" : ""}
                          onClick={() => handleChangeFlow("customer")}
                        >
                          Hizmet Alan
                        </button>
                        <button
                          type="button"
                          className={activeFlow === "provider" ? "active" : ""}
                          onClick={() => handleChangeFlow("provider")}
                        >
                          Hizmet Veren
                        </button>
                      </div>
                      <p className="onboarding-flow-tip">
                        💡 Her iki rolde de işlem yapabilirsiniz. Yukarıdan rehber akışını değiştirebilirsiniz.
                      </p>
                    </div>
                  )}

                  {nextRequiredStep ? (
                    <div className="onboarding-next">
                      <div>
                        <p className="onboarding-next-label">Sıradaki adım</p>
                        <p className="onboarding-next-title">{nextRequiredStep.title}</p>
                        <p className="onboarding-next-desc">{nextRequiredStep.description}</p>
                      </div>
                      {resolveAction(nextRequiredStep) && (
                        <button
                          type="button"
                          className="onboarding-primary-btn"
                          onClick={() => handleNavigate(resolveAction(nextRequiredStep)?.to)}
                        >
                          Devam Et
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="onboarding-next done">
                      <div>
                        <p className="onboarding-next-title">Zorunlu adımlar tamamlandı.</p>
                        <p className="onboarding-next-desc">
                          Opsiyonel adımlar ile deneyimini daha da güçlendirebilirsin.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="onboarding-steps">
                    {steps.map((step) => {
                      if (step.type === "divider") {
                        return (
                          <div key={step.id} className="onboarding-divider">
                            {step.title}
                          </div>
                        );
                      }
                      const done = isStepDone(step);
                      const isActive = nextRequiredStep?.id === step.id;
                      const action = resolveAction(step);
                      return (
                        <div
                          key={step.id}
                          className={`onboarding-step ${done ? "done" : ""} ${isActive ? "active" : ""}`}
                        >
                          <button
                            type="button"
                            className={`onboarding-step-check ${done ? "checked" : ""}`}
                            onClick={() => handleToggleStep(step)}
                            disabled={step.autoEnforced}
                            aria-label={`${step.title} tamamlandı`}
                          >
                            {done ? "✓" : "•"}
                          </button>
                          <div className="onboarding-step-content">
                            <div className="onboarding-step-title">
                              <span>{step.title}</span>
                              <span className={`onboarding-badge ${step.required ? "required" : "optional"}`}>
                                {step.required ? "Zorunlu" : "Opsiyonel"}
                              </span>
                            </div>
                            <p className="onboarding-step-desc">{step.description}</p>
                            {action && (
                              <div className="onboarding-step-actions">
                                <button
                                  type="button"
                                  className="onboarding-link-btn"
                                  onClick={() => handleNavigate(action.to)}
                                >
                                  {action.label}
                                </button>
                                {action.tour && (
                                  <button
                                    type="button"
                                    className="onboarding-show-btn"
                                    onClick={() => handleShowMe(action.to, action.tour)}
                                  >
                                    Göster ✨
                                  </button>
                                )}
                                {step.demoType && (
                                  <button
                                    type="button"
                                    className="onboarding-show-btn"
                                    onClick={() => setDemoType(step.demoType)}
                                  >
                                    Nasıl Yapılır? 🎓
                                  </button>
                                )}
                              </div>
                            )}
                            {step.details && (
                              <OnboardingSubSteps
                                details={step.details}
                                user={user}
                                counts={counts}
                                onShowMe={handleShowMe}
                                to={action?.to}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="onboarding-footer">
              <button
                type="button"
                className="onboarding-ghost-btn"
                onClick={handleNeverShow}
              >
                Bir Daha Gösterme
              </button>
              <button
                type="button"
                className="onboarding-secondary-btn"
                onClick={handleMinimize}
              >
                Daha Sonra
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="onboarding-fab"
            onClick={handleOpen}
            aria-label="Rehberi Aç"
          >
            Rehberi Aç
          </button>
        )}
      </div>
      <OnboardingDemoModal
        demoType={demoType}
        onClose={() => setDemoType(null)}
      />
    </>
  );
}
